import { useState } from 'react';
import { useLocations } from '@/hooks/useLocations';
import { useLocationStore } from '@/stores/useLocationStore';
import { useCartStore } from '@/stores/useCartStore';
import { useToastStore } from '@/stores/useToastStore';
import { MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ContextChangeWarningModal } from './ContextChangeWarningModal';
import { CustomerLocationDto } from '@/types';
import { format } from 'date-fns';
import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface LocationSelectContentProps {
    onSelect?: () => void;
}

export const LocationSelectContent = ({ onSelect }: LocationSelectContentProps) => {
    const { location, dateRange, setLocation } = useLocationStore();
    const { data: locations, isLoading } = useLocations();
    const cartItems = useCartStore((state) => state.items);
    const itemCount = useCartStore((state) => state.itemCount());
    const validateCart = useCartStore((state) => state.validateCart);
    const clearCart = useCartStore((state) => state.clearCart);
    const { warning, info } = useToastStore();

    // Warning modal state (cart re-validation notice)
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [pendingLocation, setPendingLocation] = useState<CustomerLocationDto | null>(null);

    // Incompatible-items modal state (replaces window.confirm)
    const [showIncompatibleModal, setShowIncompatibleModal] = useState(false);
    const [incompatibleCount, setIncompatibleCount] = useState(0);
    const [oldLocation, setOldLocation] = useState<CustomerLocationDto | null>(null);

    const handleLocationSelect = (newLocation: CustomerLocationDto | null) => {
        if (!newLocation) return;

        // Check if we have items in cart and location is actually changing
        if (cartItems.length > 0 && location?.id !== newLocation.id) {
            // Show warning modal instead of browser confirm
            setPendingLocation(newLocation);
            setShowWarningModal(true);
        } else {
            // No cart items or same location - proceed directly
            setLocation(newLocation);
            onSelect?.();
        }
    };

    const handleConfirmLocationChange = async () => {
        if (!pendingLocation) return;

        const prevLocation = location;
        setLocation(pendingLocation);

        // Try to validate cart with new location
        if (dateRange.from && dateRange.to && cartItems.length > 0) {
            const startDate = format(dateRange.from, 'yyyy-MM-dd');
            const endDate = format(dateRange.to, 'yyyy-MM-dd');
            const validationResult = await validateCart(startDate, endDate, pendingLocation.id);

            if (validationResult && !validationResult.isValid) {
                const itemsWithIssues = validationResult.items.filter(item => !item.isAvailable).length;
                // Store context needed by the incompatible-items modal
                setOldLocation(prevLocation);
                setIncompatibleCount(itemsWithIssues);
                setPendingLocation(pendingLocation);
                setShowWarningModal(false);
                setShowIncompatibleModal(true);
                return;
            }
        }

        onSelect?.();
        setPendingLocation(null);
        setShowWarningModal(false);
    };

    const handleClearAndContinue = () => {
        clearCart();
        warning(`Your cart was cleared and your pickup location was changed to ${pendingLocation?.name}.`);
        setShowIncompatibleModal(false);
        setPendingLocation(null);
        setOldLocation(null);
        onSelect?.();
    };

    const handleRevertLocation = () => {
        if (oldLocation) {
            setLocation(oldLocation);
            info(`Your pickup location is still ${oldLocation.name}.`);
        }
        setShowIncompatibleModal(false);
        setPendingLocation(null);
        setOldLocation(null);
    };

    return (
        <>
            <div className="p-2">
                <h3 className="mb-2 px-2 text-xs font-semibold text-muted-foreground uppercase">Pickup locations</h3>
                {isLoading ? (
                    <div className="px-2 py-4 text-sm text-muted-foreground">Loading pickup locations...</div>
                ) : (
                    <div className="space-y-1">
                        {locations?.map((loc) => (
                            <button
                                key={loc.id}
                                onClick={() => handleLocationSelect(loc)}
                                className={cn(
                                    "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-secondary",
                                    location?.id === loc.id ? "bg-primary/10 text-primary" : "text-foreground"
                                )}
                            >
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-muted-foreground">
                                    <MapPin className="h-4 w-4" />
                                </div>
                                <span className="font-medium">{loc.name}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Warning Modal — cart will be re-validated */}
            <ContextChangeWarningModal
                isOpen={showWarningModal}
                onClose={() => {
                    setShowWarningModal(false);
                    setPendingLocation(null);
                }}
                onConfirm={handleConfirmLocationChange}
                changeType="location"
                itemCount={itemCount}
                currentLocation={location?.name}
                newLocation={pendingLocation?.name}
            />

            {/* Incompatible Items Modal — some cart items unavailable at new location */}
            <Transition appear show={showIncompatibleModal} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={handleRevertLocation}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/50" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-amber-100">
                                        <AlertTriangle className="h-6 w-6 text-amber-600" />
                                    </div>

                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-semibold text-foreground text-center"
                                    >
                                        Some items aren't available here
                                    </Dialog.Title>

                                    <p className="mt-2 text-sm text-muted-foreground text-center">
                                        {incompatibleCount} item{incompatibleCount === 1 ? '' : 's'} in your cart{' '}
                                        can't be booked for pickup in {pendingLocation?.name}.
                                        To continue with this location, your cart will be cleared.
                                    </p>

                                    <div className="mt-6 flex gap-3">
                                        <Button
                                            variant="outline"
                                            onClick={handleRevertLocation}
                                            className="flex-1"
                                        >
                                            Keep my cart
                                        </Button>
                                        <Button
                                            onClick={handleClearAndContinue}
                                            className="flex-1"
                                        >
                                            Clear cart &amp; continue
                                        </Button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    );
};
